import React, { useEffect } from 'react';
import SubmitButton from './SubmitButton';
import { useLocation } from 'react-router-dom';
import {
  CheckCircleIcon,
  HandThumbUpIcon,
  MapPinIcon,
  CurrencyPoundIcon,
  ArrowRightStartOnRectangleIcon,
  Squares2X2Icon,
  ArrowRightEndOnRectangleIcon,
} from '@heroicons/react/24/solid';
import { useFlow } from '../utilities/useFlow';
import { axiosKK } from '../services/networkRequest';
import { adminCheck } from '../utilities/Role';
import { getUser, clearUser } from '../services/storage';

export default function Header(): JSX.Element {
  const { pathname } = useLocation();
  const flow = useFlow();
  const user = getUser();
  const isLoggedIn = !!user;
  const isAdmin = user ? adminCheck(user) : false;
  const dashboardPath = isAdmin ? '/admin/dashboard' : '/dashboard';

  const handleLogout = () => {
    clearUser();
    delete axiosKK.defaults.headers.common['Authorization'];
    window.location.assign('/');
  };

  useEffect(() => {
    window.fbq('track', 'PageView');
  }, [pathname]);

  return (
    <header className="App-header">
      <nav className="navbar">
        <div className="container">
          {flow === 'Keepr' ? (
            <a href="/" className="navbar-brand keepr-text-logo">
              <img
                src="/img/svg/keepr.svg"
                alt="Keepr Logo"
                className="keepr-svg-logo"
              />
            </a>
          ) : flow === 'St Annes' ? (
            <a href="/" className="navbar-brand stannes-logo">
              <img
                src="/img/svg/st-annes-fullcolour-WHITE-RGB.svg"
                alt="St Anne's College, Oxford"
                className="stannes-svg-logo"
              />
            </a>
          ) : (
            <a href="/" className="navbar-brand">
              <img
                src="/img/Kit Keeper Logo - White.png"
                width="185"
                alt="LOGO"
              />
            </a>
          )}
          <section className="nav-exclude-img">
            <ul id="primary" className="navbar-nav items">
              <li className="menu-item">
                <a
                  href={
                    flow === 'Keepr'
                      ? 'https://keepr.co'
                      : flow === 'St Annes'
                      ? 'https://www.st-annes.ox.ac.uk'
                      : '../storage/boxes'
                  }
                  target="_blank"
                  rel="noreferrer"
                  className={pathname?.includes('storage') ? 'active' : ''}
                >
                  <h5>Storage</h5>
                </a>
              </li>
              {flow !== 'Keepr' && flow !== 'St Annes' && (
                <li className="menu-item">
                  <a
                    href="../shipping/boxes"
                    target="_blank"
                    rel="noreferrer"
                    className={pathname?.includes('suitcase') ? 'active' : ''}
                  >
                    <h5>Shipping</h5>
                  </a>
                </li>
              )}
              {/*<li className="menu-item"><a href="https://www.kitkeeper.co.uk/faqs/">FAQs</a></li>*/}
              {flow !== 'Keepr' && flow !== 'St Annes' && (
                <li className="menu-item">
                  <a
                    href="https://www.kitkeeper.co.uk/blog/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <h5>News</h5>
                  </a>
                </li>
              )}
              <li className="menu-item">
                <a
                  href={
                    flow === 'Keepr'
                      ? 'https://keepr.co/#faqs'
                      : flow === 'St Annes'
                      ? 'https://www.st-annes.ox.ac.uk/current-students/'
                      : 'https://www.kitkeeper.co.uk/#faqs'
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  <h5>Support</h5>
                </a>
              </li>
              {isLoggedIn && (
                <li className="menu-item">
                  <a href={dashboardPath} className="header-login-btn" title={isAdmin ? 'Dashboard' : 'Account'}>
                    <Squares2X2Icon className="header-auth-icon" />
                    {isAdmin ? 'Dashboard' : 'Account'}
                  </a>
                </li>
              )}
              <li className="menu-item">
                {isLoggedIn ? (
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="header-login-btn"
                    title="Logout"
                  >
                    <ArrowRightStartOnRectangleIcon className="header-auth-icon" />
                    Logout
                  </button>
                ) : (
                  <a href="../login" className="header-login-btn">
                    <ArrowRightEndOnRectangleIcon className="header-auth-icon" />
                    Login
                  </a>
                )}
              </li>
            </ul>

            {/*
            <div className="pipe-div">|</div>
            */}
            {/* Social media icons hidden in header — to re-enable, add inline brand SVGs
                (Heroicons does not ship brand marks) and render inside li.social-icons elements. */}
            {/* Mobile menu bars hidden — to re-enable, import Bars3Icon from '@heroicons/react/24/solid'. */}
            <div className="header-mobile-auth">
              {isLoggedIn ? (
                <>
                  <a href={dashboardPath} className="header-mobile-auth-btn" title={isAdmin ? 'Dashboard' : 'Account'}>
                    <Squares2X2Icon className="header-mobile-auth-icon" />
                    <span className="header-mobile-auth-label">{isAdmin ? 'Dashboard' : 'Account'}</span>
                  </a>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="header-mobile-auth-btn"
                    title="Logout"
                  >
                    <ArrowRightStartOnRectangleIcon className="header-mobile-auth-icon" />
                    <span className="header-mobile-auth-label">Logout</span>
                  </button>
                </>
              ) : (
                <a href="../login" className="header-mobile-auth-btn" title="Login">
                  <ArrowRightEndOnRectangleIcon className="header-mobile-auth-icon" />
                  <span className="header-mobile-auth-label">Login</span>
                </a>
              )}
            </div>
            <div className="contact-button">
              <a
                href={
                  flow === 'Keepr'
                    ? 'https://keepr.co/#contact'
                    : flow === 'St Annes'
                    ? 'https://www.st-annes.ox.ac.uk/contact/'
                    : 'https://www.kitkeeper.co.uk/contact'
                }
                target="_blank"
                rel="noreferrer"
              >
                <SubmitButton>
                  {/* Phone icon hidden — to re-enable, import PhoneIcon from '@heroicons/react/24/solid'. */}
                  <span>Contact Us</span>
                </SubmitButton>
              </a>
            </div>
          </section>
        </div>

        <div className="containerConvincer">
          <div className="convincerContent">
            <span className="ConvincerBar1">
              <CheckCircleIcon className="convincerIcon" />
              Flexible pricing
            </span>

            <span className="ConvincerBar2">
              <HandThumbUpIcon className="convincerIcon" />
              Free £100 cover
            </span>

            <span className="ConvincerBar3">
              <CurrencyPoundIcon className="convincerIcon currencyPoundIcon" />
              <div className="innovateUkWrapper">Funded by InnovateUK</div>
            </span>

            <span className="ConvincerBar5">
              <MapPinIcon className="convincerIcon" />
              Collect & deliver anywhere in the UK
            </span>
          </div>
        </div>
      </nav>
    </header>
  );
}
